/**
 * Reservoir browser-extension install/update banner.
 *
 * Detection:
 *   - Extension's reservoir-bridge.js (MAIN world) sets window.__reservoirExt
 *     with { installed, version, … }. The version field is filled in by a
 *     postMessage from the ISOLATED-world helper after a tick or two, so we
 *     poll for up to ~2s before assuming "not installed".
 *
 * Source of truth for required version + CWS link:
 *   - GET /api/extension/manifest → { required_version, install_url }.
 *     Backend reads both from env (EXTENSION_REQUIRED_VERSION / _INSTALL_URL).
 *
 * Visibility rules:
 *   - Not installed                       → "Install" banner
 *   - Installed, version < required       → "Update available" banner
 *   - Installed, version >= required      → render nothing
 *   - Manifest fetch failed               → render nothing (don't block UI)
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Chrome, AlertTriangle, ExternalLink } from "lucide-react";

// "1.2.3" → [1,2,3] for lexicographic-on-numbers comparison. Returns
// [0,0,0] for non-strings so a missing version compares older than anything.
function parseVersion(v) {
  if (typeof v !== "string") return [0, 0, 0];
  return v.split(".").map((s) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : 0;
  });
}

function isOutdated(installed, required) {
  if (!installed || !required) return false;
  const a = parseVersion(installed);
  const b = parseVersion(required);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x < y) return true;
    if (x > y) return false;
  }
  return false;
}

// Polls window.__reservoirExt for up to ~2.4s. Returns:
//   - undefined while still checking
//   - { installed: true, version }  once detected
//   - { installed: false }          if it never appeared
function useExtensionState() {
  const [state, setState] = useState(undefined);
  useEffect(() => {
    let attempts = 0;
    const t = setInterval(() => {
      const ext = typeof window !== "undefined" ? window.__reservoirExt : null;
      if (ext?.installed) {
        // Wait one more tick if version hasn't arrived yet (ISOLATED→MAIN
        // postMessage is a separate event-loop turn). After ~600ms with
        // installed=true but no version, give up and treat as "version
        // unknown" — a build older than the version-broadcast feature.
        if (ext.version || attempts > 3) {
          setState({ installed: true, version: ext.version || null });
          clearInterval(t);
          return;
        }
      }
      if (++attempts > 12) {
        setState({ installed: false });
        clearInterval(t);
      }
    }, 200);
    return () => clearInterval(t);
  }, []);
  return state;
}

const DISMISS_KEY = "rsvr_ext_banner_dismissed_until";

function isDismissed() {
  try {
    const until = Number(window.localStorage.getItem(DISMISS_KEY) || 0);
    return Number.isFinite(until) && until > Date.now();
  } catch { return false; }
}

function dismissForADay() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
  } catch {}
}

export default function ExtensionBanner() {
  const ext = useExtensionState();
  const [manifest, setManifest] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissed());

  useEffect(() => {
    let cancelled = false;
    api.get("/extension/manifest")
      .then(({ data }) => { if (!cancelled) setManifest(data); })
      .catch(() => { /* don't block dashboard if this fails */ });
    return () => { cancelled = true; };
  }, []);

  // Still checking, or manifest fetch failed → render nothing.
  if (ext === undefined || !manifest) return null;
  if (dismissed) return null;

  const installUrl = manifest.install_url;
  if (!installUrl) return null;

  let mode = null;        // "install" | "update"
  let title = "";
  let body = "";
  let cta = "";

  if (!ext.installed) {
    mode = "install";
    title = "Install the Reservoir extension";
    body  = "The browser extension is required to sync your loyalty balances automatically.";
    cta   = "Install from the Chrome Web Store";
  } else if (isOutdated(ext.version, manifest.required_version)) {
    mode = "update";
    title = "Update available for the Reservoir extension";
    body  = `You're on v${ext.version || "older"} — v${manifest.required_version} adds new programs and fixes. Update to keep your balances syncing.`;
    cta   = "Update on the Chrome Web Store";
  } else {
    return null;
  }

  const palette = mode === "install"
    ? { bg: "bg-amber-50",  border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle }
    : { bg: "bg-sky-50",    border: "border-sky-200",   text: "text-sky-900",   icon: Chrome };
  const Icon = palette.icon;

  return (
    <div
      className={`mb-6 rounded-xl border ${palette.border} ${palette.bg} ${palette.text} px-4 py-3 flex items-start gap-3`}
      data-testid={`ext-banner-${mode}`}
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13.5px] leading-tight">{title}</div>
        <div className="text-[12.5px] mt-0.5 opacity-90">{body}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a
          href={installUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary text-[12px] inline-flex items-center gap-1.5"
          data-testid={`ext-banner-${mode}-cta`}
        >
          <ExternalLink size={12} /> {cta}
        </a>
        {mode === "update" && (
          <button
            type="button"
            className="text-[12px] text-neutral-500 hover:text-black px-2 py-1"
            onClick={() => { dismissForADay(); setDismissed(true); }}
            title="Snooze for 24 hours"
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
}
